import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { useLang } from "../context/LanguageContext";
import { CODE_TO_URL } from "../lib/i18nRouter";
import { Sparkles, Share2, MessageCircle, Copy } from "lucide-react";
import { toast } from "sonner";

const PassportLanding = () => {
  const { code } = useParams();
  const { format } = useCurrency();
  const { lang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";
  const lp = (path) => `/${slug}${path}`;
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/gem-passport/public/${code}`)
      .then(({ data }) => setData(data))
      .catch((err) => setError(err.response?.data?.detail || "Passport not found"));
  }, [code]);

  // Open Graph + Twitter meta for shareable previews
  useEffect(() => {
    if (!data) return;
    const title = `${data.holder_first_name}'s Personal Gem Passport — OPIKJEWELS`;
    const desc = `${data.primary?.name || "A singular stone"} · ${data.narrative?.slice(0, 140) || ""}`;
    const img = data.primary?.image_url || "";
    document.title = title;
    const setMeta = (selector, attr, val) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [name, key] = selector.replace("meta[", "").replace("]", "").split("=");
        el.setAttribute(name, key.replace(/['"]/g, ""));
        document.head.appendChild(el);
      }
      el.setAttribute(attr, val);
    };
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[property="og:image"]', "content", img);
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", desc);
    setMeta('meta[name="twitter:image"]', "content", img);
    setMeta('meta[name="description"]', "content", desc);
  }, [data]);

  if (error) {
    return (
      <div className="pt-40 max-w-md mx-auto px-6 text-center">
        <h1 className="font-heading text-5xl mb-4">Passport not found.</h1>
        <p className="text-white/60 mb-8">The claim code may have been mistyped or the passport revoked.</p>
        <Link to={lp("/find-your-stone")} data-testid="passport-cta-fallback" className="inline-block bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-9 py-4 hover:bg-ivory">Discover Your Own Stone</Link>
      </div>
    );
  }
  if (!data) return <div className="pt-40 text-center text-white/40">Loading…</div>;

  const shareUrl = window.location.href;
  const shareText = `${data.holder_first_name}'s Personal Gem Passport — ${data.primary?.name || ""}.`;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied");
  };
  const shareNative = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: shareText, text: data.narrative?.slice(0, 140), url: shareUrl }); }
      catch (e) { void e; }
    } else { copyLink(); }
  };

  return (
    <div data-testid="passport-landing" className="pt-28">
      {/* HERO */}
      <section className="relative h-[80vh] overflow-hidden grain mb-24">
        {data.primary?.image_url && (
          <img src={data.primary.image_url} alt={data.primary.name} className="absolute inset-0 w-full h-full object-cover scale-105" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black"></div>
        <div className="relative z-10 max-w-[1600px] mx-auto h-full px-6 md:px-12 lg:px-16 flex flex-col justify-end pb-20">
          <div className="reveal-up">
            <p className="font-accent text-[11px] tracking-[0.4em] uppercase text-gold mb-5 flex items-center gap-2">
              <Sparkles size={14} /> Personal Gem Passport · {data.claim_code}
            </p>
            <h1 className="font-heading font-light tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95]">
              {data.holder_first_name}'s<br />
              <span className="italic text-ivory">{data.primary?.name || "Stone"}.</span>
            </h1>
            {data.purpose && (
              <p className="mt-8 max-w-xl text-white/70 text-base md:text-lg font-light leading-relaxed">
                Aligned with the purpose of <span className="text-gold capitalize">{data.purpose}</span>
                {data.zodiac && <> · {data.zodiac.name}</>}
                {data.birthstone && <> · {data.birthstone.name}</>}.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* NARRATIVE */}
      <section className="max-w-3xl mx-auto px-6 mb-24 text-white/85 leading-relaxed text-lg font-light">
        <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-6">Advisory note</p>
        <p className="text-2xl text-ivory font-heading italic leading-snug mb-10">"{data.narrative}"</p>
        <div className="gold-line w-24 mb-10"></div>
        {data.zodiac && (
          <div className="mb-10">
            <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-3">{data.zodiac.name} · {data.zodiac.element}</p>
            <p>{data.zodiac.narrative}</p>
          </div>
        )}
        {data.birthstone && (
          <div className="mb-10">
            <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-3">Birthstone · {data.birthstone.name}</p>
            <p>{data.birthstone.meaning}</p>
          </div>
        )}
      </section>

      {/* PRIMARY STONE CARD */}
      {data.primary && (
        <section className="max-w-4xl mx-auto px-6 mb-24">
          <Link to={lp(`/gemstone/${data.primary.id}`)} data-testid="passport-stone-link" className="block bg-onyx border border-gold/30 group">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={data.primary.image_url} alt={data.primary.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
            </div>
            <div className="p-10">
              <p className="font-accent text-[10px] tracking-[0.28em] uppercase text-gold mb-3">The Stone</p>
              <h2 className="font-heading text-4xl mb-4">{data.primary.name}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm border-y border-white/10 py-6">
                <div><div className="text-white/40 text-xs uppercase tracking-[0.18em] font-accent">Carat</div><div className="text-ivory font-heading text-xl">{data.primary.carat} ct</div></div>
                <div><div className="text-white/40 text-xs uppercase tracking-[0.18em] font-accent">Origin</div><div className="text-ivory font-heading text-xl">{data.primary.origin}</div></div>
                <div><div className="text-white/40 text-xs uppercase tracking-[0.18em] font-accent">Lab</div><div className="text-ivory font-heading text-xl">{data.primary.certification}</div></div>
                <div><div className="text-white/40 text-xs uppercase tracking-[0.18em] font-accent">Reserve</div><div className="text-ivory font-heading text-xl">{format(data.primary.reserve_price, data.primary.currency)}</div></div>
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* CTA + SHARE */}
      <section className="bg-onyx grain relative py-24 mb-0">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5">Discover yours</p>
          <h2 className="font-heading font-light text-4xl md:text-6xl leading-tight mb-8">Find the stone connected to your destiny.</h2>
          <p className="text-white/65 leading-relaxed max-w-xl mx-auto mb-10">
            Two minutes with our AI gemologist-advisor. A complimentary 30-minute consultation for the stone that fits you.
          </p>
          <Link to={lp("/find-your-stone")} data-testid="passport-get-yours" className="inline-block bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-10 py-4 hover:bg-ivory transition-colors duration-500 mb-12">
            Get Your Own Gem Passport
          </Link>

          <div className="flex flex-wrap justify-center gap-3 mb-3">
            <button data-testid="passport-share" onClick={shareNative} className="flex items-center gap-2 border border-white/30 text-white font-accent uppercase tracking-[0.24em] text-xs px-6 py-3 hover:border-gold hover:text-gold">
              <Share2 size={14} /> Share
            </button>
            <button data-testid="passport-copy" onClick={copyLink} className="flex items-center gap-2 border border-white/30 text-white font-accent uppercase tracking-[0.24em] text-xs px-6 py-3 hover:border-gold hover:text-gold">
              <Copy size={14} /> Copy Link
            </button>
            <a data-testid="passport-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 border border-white/30 text-white font-accent uppercase tracking-[0.24em] text-xs px-6 py-3 hover:border-gold hover:text-gold">
              <MessageCircle size={14} /> WhatsApp
            </a>
          </div>
          <p className="text-white/30 text-xs font-accent tracking-[0.24em] uppercase">Claim code · {data.claim_code}</p>
        </div>
      </section>
    </div>
  );
};

export default PassportLanding;
